
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/auth';

export const useRequestLimitCheck = () => {
  const { user, profile } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLimitReached, setIsLimitReached] = useState(false);
  const [requestCount, setRequestCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  
  // For free tier, limit is 3 requests per month
  const FREE_TIER_LIMIT = 3;

  const checkRequestLimit = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Get current user's subscription tier from profile
      const subscriptionTier = profile?.subscription_tier || 'free';
      
      console.log('=== REQUEST LIMIT CHECK ===');
      console.log('User subscription tier:', subscriptionTier);
      
      // If user is on pro tier, no limits apply
      if (subscriptionTier === 'pro') {
        console.log('Pro tier user - unlimited requests');
        setIsLimitReached(false);
        setRequestCount(0);
        return;
      }
      
      console.log('Free tier user - checking monthly limit');
      
      // Count requests from the current month for free tier users
      const now = new Date();
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      const { data, error: countError, count } = await supabase
        .from('refund_requests')
        .select('id', { count: 'exact' })
        .eq('user_id', user.id)
        .gte('created_at', firstDayOfMonth.toISOString());
        
      if (countError) {
        throw countError;
      }
      
      const currentCount = count || 0;
      setRequestCount(currentCount);
      
      console.log('Current month requests:', currentCount);
      console.log('Free tier limit:', FREE_TIER_LIMIT);
      
      // Check if limit is reached
      if (currentCount >= FREE_TIER_LIMIT) {
        console.log('Free tier limit reached');
        setIsLimitReached(true);
      } else {
        console.log('Within free tier limit');
        setIsLimitReached(false);
      }
    } catch (err) {
      console.error('Error checking request limit:', err);
      setError('Failed to check request limits. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    if (user && profile) {
      checkRequestLimit();
    }
  }, [user, profile]);
  
  const isProTier = profile?.subscription_tier === 'pro';
  
  return {
    isLoading,
    isLimitReached: isProTier ? false : isLimitReached, // Pro users never hit limits
    requestCount: isProTier ? 0 : requestCount, // Pro users don't need to track count
    requestsRemaining: isProTier ? Infinity : Math.max(0, FREE_TIER_LIMIT - requestCount),
    limitPerMonth: isProTier ? Infinity : FREE_TIER_LIMIT,
    error,
    checkRequestLimit,
    isProTier
  };
};
